<template>
  <div
    class="row mw-node-card-header"
    :class="
      focusNavItem && focusNavItem.type !== 'Diagnostic'
        ? 'content-header'
        : 'diag-content-header'
    "
  >
    <!-- SHOW SEARCH RESULTS -->
    <div
      v-if="showSearch"
      class="d-flex align-items-center justify-content-between gap-2 w-full px-3"
    >
      <div class="d-flex align-items-center px-0 py-3 search-prompt">
        <font-awesome-icon
          class="large-icon mw-search-icon"
          :icon="['fas', 'search']"
        ></font-awesome-icon>
        <h2 class="mr-2 text-nowrap">{{ $t('mayday-web.search-results') }}</h2>
        <h2 class="text-nowrap">{{ searchQuery }}</h2>
      </div>
      <div class="text-right pr-0">
        <font-awesome-icon
          class="grey-hover-blue"
          :icon="['fad', 'times-circle']"
          @click="resetSearch"
        ></font-awesome-icon>
      </div>
    </div>

    <!-- CLASSIC NAVIGATION -->
    <div v-else class="d-flex col-12 align-items-start p-3">
      <!-- TYPE ICON + TITLE -->
      <div class="d-flex col-10 align-items-start px-0">
        <div class="icon-wrapper">
          <IconWrapper
            v-if="focusNavItem && !focusNavItem.type"
            :icon="focusNavItem.icon"
            :mini-icon="focusNavItem.miniIcon"
            :size="24"
            class="mr-3"
          ></IconWrapper>
          <font-awesome-icon
            v-else-if="focusNavItem && focusNavItem.type === 'Step'"
            class="large-icon"
            :icon="['fad', 'level-up']"
          ></font-awesome-icon>
          <img
            v-else
            :src="getIcon(focusNavItem ? focusNavItem.type : null)"
            alt="not_found"
            class="large-icon"
          />
        </div>

        <div>
          <h2>
            {{ focusNavItem ? focusNavItem.label : $t('mayday-web.search') }}
          </h2>
          <div
            class="d-flex align-items-center full-labels mr-1"
            v-if="
              focusNavItem && focusNavItem.labels && focusNavItem.labels.length
            "
          >
            <ContentListItemFullLabelWrapper :labels="focusNavItem.labels" />
          </div>
        </div>
      </div>

      <!-- ACTIONS ICONS -->
      <div class="col-2 label-actions pr-0">
        <el-tooltip
          v-if="focusNavItem && focusNavItem.type === 'Diagnostic'"
          :content="$t('mayday-web.logigram-tooltip')"
          placement="top"
        >
          <font-awesome-icon
            class="grey-hover-blue"
            :icon="['fad', 'project-diagram']"
            @click="showDiagArchi = true"
          ></font-awesome-icon>
        </el-tooltip>
        <el-tooltip
          v-if="
            focusNavItem &&
            ['Article', 'Diagnostic'].includes(focusNavItem.type)
          "
          :content="
            $t(
              focusItemIsFavorite
                ? 'mayday-web.star-tooltip.out'
                : 'mayday-web.star-tooltip.in',
            )
          "
          placement="top"
        >
          <font-awesome-icon
            class="ml-2"
            :class="[focusItemIsFavorite ? 'yellow-icon' : 'grey-hover-yellow']"
            :icon="['fad', 'star']"
            @click="updateFavorite(focusNavItem)"
          ></font-awesome-icon>
        </el-tooltip>
        <el-tooltip
          v-if="hasEditionRights"
          :content="$t('mayday-web.edit-tooltip')"
          placement="top"
        >
          <font-awesome-icon
            class="grey-hover-blue ml-2"
            :icon="['fad', 'pen']"
            @click="editCurrentEntity()"
          ></font-awesome-icon>
        </el-tooltip>
      </div>
    </div>
    <DiagArchiModal
      v-if="showDiagArchi"
      :display="showDiagArchi"
      :diagnostic-id="focusNavItem.id"
      :display-options="displayOptions"
      @close="showDiagArchi = false"
      @relocate-child="$emit('relocate-child', $event)"
    />
  </div>
</template>
<script>
import { mapGetters, mapActions } from 'vuex';
import DiagArchiModal from 'components/Modals/DiagArchiModal';
import IconWrapper from '@/components/IconWrapper';
import ContentListItemFullLabelWrapper from '@/components/ListItems/ContentListItemFullLabelWrapper';

export default {
  name: 'mayday-node-main-header',
  components: {
    DiagArchiModal,
    IconWrapper,
    ContentListItemFullLabelWrapper,
  },
  data() {
    return {
      showDiagArchi: false,
      displayOptions: {
        showFooterSwitches: false,
        showAliases: false,
        showAutomations: false,
        showTriggers: true,
      },
    };
  },
  computed: {
    focusItemIsFavorite() {
      return this.itemIsFavorite(this.focusNavItem.id);
    },
    ...mapGetters('webKnowledgeModule', [
      'focusKnowledge',
      'focusNavItem',
      'hasEditionRights',
      'searchQuery',
      'showSearch',
      'navigationLanguage',
    ]),
    ...mapGetters('userFavoritesModule', ['itemIsFavorite']),
  },
  methods: {
    getIcon(type) {
      switch (type) {
        case 'Article':
          return require('@/assets/article_icon.svg');
        case 'Diagnostic':
          return require('@/assets/diag_icon.svg');
        case 'Step':
          return require('@/assets/diag_icon.svg');
        case 'keyStep':
          return require('@/assets/diag_icon.svg');
        default:
          return require('@/assets/article_icon.svg');
      }
    },
    resetSearch() {
      this.storeSearchAbortEvent();
      this.updateSearchQuery('');
    },
    editCurrentEntity() {
      const entityType = this.focusNavItem.type ? 'content' : 'case';
      const entityId = this.focusNavItem.id;

      const { stepId } = this.$route.params;
      const stepPath = stepId ? `/step/${stepId}` : '';

      const path = `/knowledge/${entityType}/${entityId}${stepPath}?lang=${this.navigationLanguage}`;
      this.$router.push({ path: path });
    },
    updateFavorite(entity) {
      const favorite = {
        knowledgeId: this.focusKnowledge.id,
        entityId: entity.id,
        entityType: entity.type ? entity.type : 'Case',
      };
      this.updateUserFavorites(favorite);
    },
    ...mapActions('webKnowledgeModule', [
      'updateUserFavorites',
      'updateSearchQuery',
      'storeSearchAbortEvent',
    ]),
  },
};
</script>
<style lang="scss" scoped>
.content-header {
  border-bottom: 2px solid #f2f6fd;
}

.search-prompt {
  width: 100%;
  min-width: 0;
  overflow: hidden;
  h2:last-of-type {
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.mw-node-card-header {
  h2 {
    font-size: 22px;
    letter-spacing: 1px;
    margin: 0;
    word-wrap: break-word;
  }
  .large-icon {
    margin-right: 16px;
    width: 24px;
    height: 24px;
    color: $grey-4-mayday;
  }
}
.mw-search-icon {
  color: $grey-4-mayday;
}
.grey-hover-yellow {
  color: $grey-4-mayday;
  &:hover {
    color: $yellow-mayday;
    cursor: pointer;
  }
}
.yellow-icon {
  color: $yellow-mayday;
  cursor: pointer;
}
.grey-hover-blue {
  color: $grey-4-mayday;
  &:hover {
    color: $blue-mayday;
    cursor: pointer;
  }
}

.label-actions {
  height: 33px;
  display: flex;
  align-items: center;
  justify-content: right;
}

.icon-wrapper {
  height: 33px;
  display: flex;
  align-items: center;
}
</style>
